import React, { useState, useRef } from 'react';
import { useController, Control } from 'react-hook-form';
import { cn } from 'lib/utils';
import { Icon } from 'components/ui';
import { isAllowedFileType } from 'lib/fileTypeUtils';
import { supabase } from 'config/supabase';

interface FileUploaderProps {
  name: string;
  control: Control<any, any>;
  label?: string;
  accept?: string;
  maxSize?: number;
  multiple?: boolean;
  className?: string;
  disabled?: boolean;
  onFileRemoved?: (materialId: string) => void; // Callback for tracking deletions
}

interface UploadedFile {
  file: File | {};
  id: string;
  name: string;
  size: number;
  materialId?: string; // Database ID from brand_materials
  storageUrl?: string; // Storage path
}

const FileUploader: React.FC<FileUploaderProps> = ({
  name,
  control,
  label,
  accept = '.pdf,.doc,.docx,.txt',
  maxSize = 10 * 1024 * 1024, // 10MB default
  multiple = true,
  className,
  disabled = false,
  onFileRemoved,
}) => {
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    defaultValue: [],
  });

  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadedFiles: UploadedFile[] = field.value || [];

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const validateFile = (file: File) => {
    // Check file type first
    if (!isAllowedFileType(file)) {
      alert(
        `File ${file.name} has an unsupported format. Please upload PDF, TXT, DOC, or DOCX files.`,
      );
      return false;
    }

    // Check file size
    if (file.size > maxSize) {
      alert(`File ${file.name} is too large. Maximum size is ${formatFileSize(maxSize)}`);
      return false;
    }

    return true;
  };

  const handleFiles = (files: FileList) => {
    const validFiles: UploadedFile[] = [];

    Array.from(files).forEach((file) => {
      if (validateFile(file)) {
        validFiles.push({
          file,
          id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
          name: file.name,
          size: file.size,
        });
      }
    });

    const newFiles = multiple ? [...uploadedFiles, ...validFiles] : validFiles;
    field.onChange(newFiles);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    if (!disabled && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
    }
  };

  const removeFile = (fileId: string) => {
    const fileToRemove = uploadedFiles.find((f) => f.id === fileId);
    const newFiles = uploadedFiles.filter((f) => f.id !== fileId);
    field.onChange(newFiles);

    // Track deletion if this file was already uploaded to backend
    if (fileToRemove?.materialId && onFileRemoved) {
      onFileRemoved(fileToRemove.materialId);
    }
  };

  const downloadFile = async (file: UploadedFile) => {
    // If file has a storageUrl (autofilled or existing file), download from storage
    if (file.storageUrl) {
      try {
        // Use Supabase's authenticated download for private bucket
        const { data, error } = await supabase.storage
          .from('brand-materials')
          .download(file.storageUrl);

        if (error) {
          console.error('Error downloading file:', error);
          alert('Failed to download file. Please try again.');
          return;
        }

        // Create a blob URL and trigger download
        const url = URL.createObjectURL(data);
        const link = document.createElement('a');
        link.href = url;
        link.download = file.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error downloading file:', error);
        alert('Failed to download file. Please try again.');
      }
    }
    // If file is a File blob (newly uploaded), create object URL and download
    else if (file.file instanceof File) {
      const url = URL.createObjectURL(file.file);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const openFileDialog = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className={cn('space-y-3', className)}>
      {label && <label className="block text-sm text-text font-medium">{label}</label>}

      {/* Upload area */}
      <div
        className={cn(
          'border bg-white rounded-lg p-8 text-center cursor-pointer transition-colors',
          isDragging ? 'border-primary bg-primary/5' : 'border-gray-300 hover:border-gray-400',
          disabled && 'opacity-50 cursor-not-allowed',
          error && 'border-destructive',
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={openFileDialog}
      >
        <div className="space-y-2">
          <div className="mx-auto flex gap-2 items-center justify-center">
            <Icon type="upload" className="w-4" />
            <p className="text-sm font-medium text-gray-900">Upload Files</p>
          </div>

          <div className="flex gap-2 flex-col">
            <p className="text-sm hidden text-gray-500">
              {multiple ? 'Upload multiple files' : 'Upload single file'} • Max{' '}
              {formatFileSize(maxSize)}
            </p>
            <p className="text-xs font-medium text-gray-400">
              Accepted formats: <span className="uppercase">{accept.split(',').join(', ')} </span>
              (Max 10MB)
            </p>
          </div>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleFileSelect}
        disabled={disabled}
        className="hidden"
      />

      {/* File list */}
      {uploadedFiles.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">
            Uploaded Files ({uploadedFiles.length})
          </p>
          {uploadedFiles.map((file) => (
            <div
              key={file.id}
              className="group flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <svg
                    className="w-4 h-4 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{file.name}</p>
                  <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {/* Download button - visible on hover */}
                <button
                  type="button"
                  onClick={() => downloadFile(file)}
                  disabled={disabled}
                  className="opacity-0 group-hover:opacity-100 transition-opacity text-blue-600 hover:text-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Download file"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                </button>

                {/* Delete button - always visible */}
                <button
                  type="button"
                  onClick={() => removeFile(file.id)}
                  disabled={disabled}
                  className="text-red-500 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Remove file"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {error && <p className="text-sm text-destructive">{error.message}</p>}
    </div>
  );
};

export { FileUploader };
